import React from "react";
import { Phone, Mail, MapPin } from "lucide-react";
import { useLang } from "../../i18n/LanguageContext";
import { S } from "../../data/strings";
import { CONTACT, waLink, WA } from "../../data/content";
import { Overline, GoldDivider } from "./Ornament";
import { WhatsappIcon } from "./Header";
import { Reveal } from "../../lib/motion";

export const Contact = () => {
  const { t, lang } = useLang();

  const cards = [
    {
      icon: WhatsappIcon,
      label: { en: "WhatsApp", hi: "व्हाट्सएप" },
      value: CONTACT.phone,
      href: waLink(WA.general),
      external: true,
    },
    {
      icon: Phone,
      label: S.contact.phone,
      value: CONTACT.phone,
      href: `tel:${CONTACT.phone}`,
      external: false,
    },
    {
      icon: Mail,
      label: S.contact.email,
      value: CONTACT.email,
      href: `mailto:${CONTACT.email}`,
      external: false,
    },
    {
      icon: MapPin,
      label: S.contact.address,
      value: lang === "hi" ? CONTACT.addressHi : CONTACT.addressEn,
      href: null,
      external: false,
    },
  ];

  return (
    <section id="contact" data-testid="contact-section" className="relative bg-ivory grain py-24 sm:py-32">
      <div className="mx-auto max-w-[1400px] px-6 sm:px-10">
        <div className="text-center max-w-2xl mx-auto">
          <Overline>{t(S.contact.overline)}</Overline>
          <h2 className="mt-4 font-serif font-light text-maroon leading-[1.1] tracking-tight text-3xl sm:text-4xl lg:text-5xl">
            {t(S.contact.heading)}
          </h2>
          <p className="mt-5 text-maroon/70 text-sm sm:text-base">{t(S.contact.sub)}</p>
          <GoldDivider className="mt-7" />
        </div>

        {/* Prominent WhatsApp CTA */}
        <Reveal className="mt-14 max-w-3xl mx-auto">
          <a
            href={waLink(WA.general)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-whatsapp-button"
            className="group flex flex-col sm:flex-row items-center justify-between gap-6 rounded-lg bg-maroon p-7 sm:p-9 text-ivory hover:bg-wine transition-colors duration-300 text-center sm:text-left"
          >
            <div className="flex flex-col sm:flex-row items-center gap-5">
              <span className="flex items-center justify-center w-14 h-14 rounded-full bg-gold text-burgundy shrink-0">
                <WhatsappIcon className="w-7 h-7" />
              </span>
              <div>
                <p className="font-serif text-2xl leading-tight">{t(S.contact.waBtn)}</p>
                <p className="text-ivory/70 text-sm mt-1">{CONTACT.phone}</p>
              </div>
            </div>
            <span className="hidden sm:inline text-goldlight text-3xl transition-transform duration-300 group-hover:translate-x-1">&#8594;</span>
          </a>
        </Reveal>

        {/* Info cards */}
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 max-w-5xl mx-auto">
          {cards.map((c, i) => {
            const Icon = c.icon;
            const inner = (
              <div className="h-full flex flex-col items-center text-center gap-3 bg-white/70 border border-gold/20 rounded-lg px-5 py-8 transition-all duration-500 hover:border-gold/50 hover:-translate-y-1">
                <span className="flex items-center justify-center w-12 h-12 rounded-full border border-gold/40 text-gold">
                  <Icon className="w-5 h-5" strokeWidth={1.5} />
                </span>
                <p className="font-cinzel text-[10px] tracking-luxe uppercase text-gold">{t(c.label)}</p>
                <p className="text-maroon/80 text-sm leading-relaxed break-words">{c.value}</p>
              </div>
            );
            return c.href ? (
              <a
                key={i}
                href={c.href}
                data-testid={`contact-info-${i}`}
                {...(c.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                className="block"
              >
                {inner}
              </a>
            ) : (
              <div key={i} data-testid={`contact-info-${i}`}>{inner}</div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
